/**
 * ANN-508 / ANN-597 "My Vouchers" cart picker (DBM Vouchers spec §3.4, VOU-D-03; api_contract.md v0.3).
 *
 * MODE-TOLERANT (cutover-transitional): ONE asset serves BOTH stores while the estate cuts over.
 *   1. The BFF answers 404 (the voucher surface is not deployed on this store / feature off)
 *      -> the picker renders NOTHING. The container starts hidden and is only revealed once a
 *      well-formed voucher payload arrives, so a store without the feature never shows the block.
 *   2. Vouchers that carry `code` (v0.2 codes mode — retired; production runs function mode since the 12 Sep 2026 cutover) -> the legacy single-apply
 *      discount-code flow below, byte-for-byte unchanged:
 *        GET  /apps/consultant/api/extension/vouchers/available -> { vouchers[{...code}], auto_apply }
 *        POST /apps/consultant/api/extension/vouchers/apply {voucher_id} -> { code } | 409 | 410
 *      then POST /cart/update.js { discount: "<code>" } (REPLACE — one voucher code per order).
 *   3. Vouchers WITHOUT `code` (v0.3 function mode — the production store since 12 Sep 2026) -> the multi-select picker:
 *        GET  /apps/consultant/api/extension/vouchers/available
 *               -> { vouchers[], selected[], max_per_order, auto_apply, signed_in }
 *        POST /apps/consultant/api/extension/vouchers/select { voucher_ids: [...] }
 *               -> { selected[], cart_attribute:{key,value}, ensure_lines:[{voucher_id,variant_id,qty}] }
 *      The theme then writes the cart attribute EXACTLY as returned via /cart/update.js (the key and
 *      value are NEVER composed client-side), ensures each free-product reward line via /cart/add.js
 *      (customer-added lines are never removed), re-reads /cart.js and renders the applied state from
 *      Shopify's own `dbm-voucher-<id>` / `dbm-vouchers-<id1>+<id2>` discount allocations
 *      (AC-VOU-093 parity: success is only what Shopify confirms, not what DBM said).
 *      On LOAD (annique-issues#216) the SAME attribute is put BACK when the cart carries none:
 *      `available` returns the identical server-composed `cart_attribute` + `ensure_lines`, because
 *      `voucher_selections` outlives an order while a fresh Shopify cart carries no attributes — so
 *      without it her second order paid full price with the voucher still ticked. Write only into
 *      the VOID: an attribute already on the cart is never overwritten and an empty selection is
 *      never written back, which is what makes the restore neither a loop nor a fight with the
 *      buyer (see shouldRestoreSelection).
 *      VALUE vouchers (amount/percent) spread proportionally across paid lines, which reads as
 *      per-line discounting — so their per-line badges are hidden (via the data-dbm-line-discount
 *      hook in snippets/line-item.liquid) and replaced by NOP-style summary rows in the picker
 *      box: one nominal label row per applied value voucher + a "Voucher savings" total summed
 *      from the actual allocations (the total row is the truth; free_product line badges stay).
 *
 * GRACEFUL DEGRADE stays the prime directive (mirrors dbm-gwp.js): signed-out / no vouchers /
 * feature off / ANY pre-reveal failure => the picker stays hidden and the cart is untouched.
 */
(function () {
  'use strict';

  var AVAILABLE = '/apps/consultant/api/extension/vouchers/available';
  var APPLY = '/apps/consultant/api/extension/vouchers/apply';
  var SELECT = '/apps/consultant/api/extension/vouchers/select';
  var AUTO_KEY = 'dbm_vouchers_autoapplied'; // sessionStorage: the voucher we auto-applied/selected this session
  var RESTORE_KEY = 'dbm_vouchers_restored'; // sessionStorage: the cart+value we already restored AND reloaded for
  var SEEN_KEY = 'dbm_vouchers_reward_seen';       // #218 r3: cart+voucher whose reward line we have SEEN in the cart
  var REFUSED_KEY = 'dbm_vouchers_reward_refused'; // #218 r3: ...and which she then DELETED — never put it back
  var NOTICE_KEY = 'dbm_vouchers_notice';          // #218 r3: a one-shot notice that must survive a cart-page reload

  var lastData = null; // the last /available payload, so a cart:refresh can re-render without re-deciding
  var lastCart = null;
  var pendingSelection = null;
  var lastSelected = []; // v0.3 multi mode: the server-confirmed selection (ids)
  var multiBusy = false; // a /select round-trip is in flight — block re-entry
  var featureOff = false; // the BFF 404'd: the voucher surface is not deployed on this store
  var revealed = false; // the picker has shown a real payload at least once on this page
  var rewardAddFailed = false; // an ensure_lines add failed (e.g. sold out) — surface after render
  var rewardRemoveFailed = false; // #218 S1 — a reward-line REMOVAL was refused; she is being charged for it
  var restoreBusy = false; // a #216 load-path attribute restore is in flight — never re-enter it
  var restoreGate = null;  // #218 — that restore's promise; a user write QUEUES behind it (see afterRestore)
  var selectionEpoch = 0;  // #218 — bumped by every USER-initiated selection change (tick, untick, clear-all)
  var reconcileBusy = false; // #218 — a load-path reward-line reconcile is in flight
  var reconcileGate = null;  // #218 S1 — that reconcile's promise; a user write QUEUES behind it too
  var rewardOpAttempts = {}; // #218 S1 — reward-line ops already TRIED this page view (see claimRewardOps)

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-dbm-vouchers]'));
  }

  function reveal() {
    revealed = true;
    roots().forEach(function (c) { c.hidden = false; });
  }

  function conceal() {
    revealed = false;
    roots().forEach(function (c) { c.hidden = true; });
  }

  function isCartPage() {
    try {
      return window.themeVariables && window.themeVariables.settings &&
        window.themeVariables.settings.pageType === 'cart';
    } catch (e) { return false; }
  }

  function fetchAvailable() {
    return fetch(AVAILABLE, { credentials: 'include', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .catch(function () { return { network: true }; });
  }

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // APPLIED means Shopify is actually discounting the cart — NOT merely that the code is
  // attached to it. Shopify keeps a code on the cart with `applicable: false` when the cart
  // does not meet the discount's own requirements (below minimum spend, wrong audience,
  // usage limit reached), and checkout then correctly ignores it.
  //
  // Counting those as applied is what produced the UAT defect: a R303.20 cart offered the
  // R100 UAT-AMOUNT-R100 voucher (min spend R500), checked its radio, and reported
  // "A voucher is applied to this cart" while the total never moved and the code was not
  // carried to checkout. applyCode() already tests `d.applicable` (see the !applied branch,
  // which surfaces "Spend R500.00+ to use this voucher") — but every later repaint went
  // through here and silently overturned it.
  function appliedCodes(cart) {
    var set = {};
    var dc = cart && cart.discount_codes;
    if (Array.isArray(dc)) {
      dc.forEach(function (d) {
        if (d && d.code && d.applicable !== false) { set[String(d.code).toLowerCase()] = true; }
      });
    }
    return set;
  }

  // v0.3 mode switch. Per the locked contract a payload carries per-voucher `code` ONLY in codes
  // mode, so ANY code present = legacy single-apply flow; no codes = the multi-select flow.
  function isMultiMode(data) {
    var rows = data && Array.isArray(data.vouchers) ? data.vouchers : [];
    if (!rows.length) { return false; }
    return !rows.some(function (v) { return !!(v && v.code); });
  }

  // v0.3 applied state comes from Shopify's own discount allocations on the re-read cart —
  // titles `dbm-voucher-<id>` (one voucher) or `dbm-vouchers-<id1>+<id2>` (a stacked pair) —
  // scanning both cart-level applications and line-level allocations.
  function appliedVoucherIds(cart) {
    var set = {};
    function scan(title) {
      var m = /^dbm-vouchers?-(.+)$/.exec(String(title || ''));
      if (!m) { return; }
      m[1].split('+').forEach(function (id) { if (id) { set[id] = true; } });
    }
    var apps = cart && cart.cart_level_discount_applications;
    if (Array.isArray(apps)) {
      apps.forEach(function (a) { scan(a && a.title); });
    }
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      var allocs = item && item.line_level_discount_allocations;
      if (Array.isArray(allocs)) {
        allocs.forEach(function (a) { scan(a && a.discount_application && a.discount_application.title); });
      }
    });
    return set;
  }

  // Pure selection arithmetic: toggle `id` in/out of `current`, enforcing the cap.
  function nextSelection(current, id, checked, cap) {
    var ids = (Array.isArray(current) ? current : []).filter(function (x) { return String(x) !== String(id); });
    if (checked) {
      if (ids.length >= cap) { return { error: 'cap' }; }
      ids.push(id);
    }
    return { ids: ids };
  }

  // Owned reward lines (marked `_dbm_voucher_id`) that must leave the cart: their voucher is no
  // longer selected, OR it is selected but the cart no longer clears that voucher's own floor
  // (annique-issues#218 — a reward line below the floor is priced at FULL RETAIL, so leaving it
  // there charges her for a gift the Function refuses to discount).
  // Lines WITHOUT the marker are customer-added and are NEVER returned here.
  // `belowFloorSet` carries only POSITIVE below-floor determinations; a voucher whose floor we
  // could not resolve is absent from it, so an unknown floor never deletes a line she may own.
  function linesToRemove(cart, selectedSet, belowFloorSet) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var below = belowFloorSet || {};
    return items.filter(function (item) {
      var owner = item && item.properties && item.properties._dbm_voucher_id;
      if (!owner) { return false; }
      var key = String(owner);
      return !selectedSet[key] || below[key] === true;
    });
  }

  // ─────────────── annique-issues#218: a reward line may not pay for its own floor ───────────────
  //
  // The floor base for the reward gate: buyer-funded goods ONLY. A line carrying `_dbm_voucher_id`
  // is a reward line the picker itself put there, and it is the very line under decision.
  //
  // ⛔ THIS EXCLUSION IS THE TERMINATION PROOF, not a nicety. Because adding or removing a reward
  // line cannot change this number, the gate's verdict is a pure function of what the BUYER put in
  // the cart. Reconciling reward lines therefore cannot change its own verdict, so add→re-render→add
  // and remove→re-render→add cannot oscillate. The Function draws the same line server-side
  // (floor_base_cents skips voucher-endorsed lines), so the two sides agree on one number.
  //
  // `original_line_price` deliberately, never `final_line_price`: a value voucher we caused would
  // otherwise shrink the base that decides whether that voucher applies — the same self-reference
  // by another route.
  //
  // ClickUp 869fceq2d (Annique, 22 + 26 Oct 2026) — every voucher's minimum is measured on the TOTAL
  // RSP, not on what she pays after her consultant discount: 7 × R125 (Total RSP R875, she pays R700)
  // clears an R800 floor. A line counts quantity × its RSP (the product's dbm.fn_pricing price in
  // force, keyed by line key), never less than its own price; a line priced R0 (gift, award) still
  // counts nothing; a line without a known RSP counts its own price. The Function's `floor_rsp_cents`
  // is the same rule, so the note and checkout agree.
  function rewardFloorBaseCents(cart, rspUnitByKey) {
    var rsp = rspUnitByKey;
    if (rsp === undefined) {
      rsp = typeof readRspUnitByKey === 'function' ? readRspUnitByKey() : null;
    }
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    return items.reduce(function (sum, item) {
      if (!item) { return sum; }
      if (item.properties && item.properties._dbm_voucher_id) { return sum; } // never its own floor
      var cents = [item.original_line_price, item.final_line_price, item.line_price]
        .map(Number).find(function (n) { return isFinite(n); });
      if (!isFinite(cents)) { return sum; }
      var unit = rsp && item.key != null ? Number(rsp[item.key]) : NaN;
      var qty = Number(item.quantity);
      if (cents > 0 && isFinite(unit) && unit > 0 && isFinite(qty) && qty > 0) {
        cents = Math.max(cents, Math.round(unit * qty));
      }
      return sum + cents;
    }, 0);
  }

  // The per-unit RSP in cents of each cart line on the page, keyed by line key, from the
  // `data-dbm-offer-line` facts snippets/line-item.liquid renders (the product's dbm.fn_pricing blob):
  // campaign_override_inc_vat when above zero, else retail_inc_vat — the offers' unitRsp rule.
  function readRspUnitByKey() {
    var map = {};
    if (typeof document === 'undefined' || !document.querySelectorAll) { return map; }
    var nodes = document.querySelectorAll('[data-dbm-offer-line]');
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute('data-key');
      var raw = nodes[i].getAttribute('data-fn-pricing');
      if (!key || map[key] != null || !raw) { continue; }
      var blob = null;
      try { blob = JSON.parse(raw); } catch (e) { blob = null; }
      if (typeof blob === 'string') { try { blob = JSON.parse(blob); } catch (e) { blob = null; } }
      if (!blob || typeof blob !== 'object') { continue; }
      var unit = [blob.campaign_override_inc_vat, blob.retail_inc_vat]
        .map(function (v) { return typeof v === 'string' ? Number(v) : NaN; })
        .find(function (n) { return isFinite(n) && n > 0; });
      if (unit != null) { map[key] = Math.round(unit * 100); }
    }
    return map;
  }

  // That voucher's own minimum spend, in cents. The server's figure on the ensure line wins; the
  // offered row is the fallback (the BFF derives the list, the ticks and ensure_lines from ONE
  // filtered set, so the row is present by construction) — the two orders of preference cover a
  // theme/BFF deploy skew in either direction. Neither resolving returns null: an UNKNOWN floor is
  // not a cleared floor.
  function ensureLineFloorCents(line, vouchers) {
    if (!line) { return null; }
    var direct = Number(line.min_spend);
    if (line.min_spend != null && isFinite(direct)) { return Math.round(direct * 100); }
    var id = String(line.voucher_id);
    var row = (Array.isArray(vouchers) ? vouchers : []).filter(function (v) {
      return v && v.voucher_id != null && String(v.voucher_id) === id;
    })[0];
    if (!row) { return null; }
    var rowMin = Number(row.min_spend);
    return row.min_spend != null && isFinite(rowMin) ? Math.round(rowMin * 100) : null;
  }

  // Split the server's ensure_lines PER VOUCHER against that voucher's own floor.
  //   · `add`   — floor known AND cleared: the line may be added.
  //   · `below` — floor known AND NOT cleared: never add, and remove one already there.
  //   · neither — floor unknown: never add, never remove (fail safe in both directions).
  // ⚠️ Gating is PER VOUCHER by construction. A free_shipping / amount / percent selection
  // contributes no ensure line at all, so it can never be held back by a free_product's floor.
  function classifyEnsureLines(cart, ensureLines, vouchers) {
    var base = rewardFloorBaseCents(cart);
    var add = [];
    var below = {};
    (Array.isArray(ensureLines) ? ensureLines : []).forEach(function (line) {
      if (!line || line.voucher_id == null) { return; }
      var floor = ensureLineFloorCents(line, vouchers);
      if (floor == null) { return; }
      if (base >= floor) { add.push(line); } else { below[String(line.voucher_id)] = true; }
    });
    return { add: add, below: below, base: base };
  }

  // ensure_lines that are genuinely missing: no owned line for the voucher AND no existing
  // line (customer-added included) already carrying that variant — never add a duplicate.
  function linesToAdd(cart, ensureLines) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    return (Array.isArray(ensureLines) ? ensureLines : []).filter(function (line) {
      if (!line || !line.variant_id) { return false; }
      return !items.some(function (item) {
        if (!item) { return false; }
        if (item.properties && String(item.properties._dbm_voucher_id || '') === String(line.voucher_id)) { return true; }
        var vid = item.variant_id != null ? item.variant_id : item.id;
        return String(vid) === String(line.variant_id);
      });
    });
  }

  // ─────────── annique-issues#218 round 3: small durable stamps, keyed cart + voucher ───────────
  // sessionStorage, not memory, because applySelection reloads the cart page and these facts have
  // to survive that. The cart TOKEN is in every key, so a new cart carries none of them forward.
  function stampKey(cart, voucherId) {
    return String((cart && cart.token) || '') + '|' + String(voucherId);
  }

  function stampSet(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}') || {}; } catch (e) { return {}; }
  }

  function stamped(key, id) {
    return stampSet(key)[id] === 1;
  }

  function stampAdd(key, id) {
    try {
      var m = stampSet(key);
      if (m[id] !== 1) { m[id] = 1; sessionStorage.setItem(key, JSON.stringify(m)); }
    } catch (e) {}
  }

  function stampDrop(key, id) {
    try {
      var m = stampSet(key);
      if (m[id] === 1) { delete m[id]; sessionStorage.setItem(key, JSON.stringify(m)); }
    } catch (e) {}
  }

  // A notice that has to outlive applySelection's cart-page reload. Consumed on the first render
  // after it, then gone — a stale explanation is worse than none.
  function stashNotice(text) {
    try { sessionStorage.setItem(NOTICE_KEY, String(text)); } catch (e) {}
  }

  function takeNotice() {
    try {
      var t = sessionStorage.getItem(NOTICE_KEY);
      if (t) { sessionStorage.removeItem(NOTICE_KEY); }
      return t || null;
    } catch (e) { return null; }
  }

  function capOf(data) {
    var n = Number(data && data.max_per_order);
    return isFinite(n) && n > 0 ? n : 2;
  }

  function voucherTypeMap(vouchers) {
    var map = {};
    (Array.isArray(vouchers) ? vouchers : []).forEach(function (v) {
      if (v && v.voucher_id != null) { map[String(v.voucher_id)] = String(v.type || ''); }
    });
    return map;
  }

  // A per-line `dbm-voucher-*` badge is hidden ONLY when every voucher id in its title is a
  // known VALUE voucher (amount/percent — Shopify spreads those proportionally across paid
  // lines, which reads as if the financials were per-line-discounted). free_product badges
  // stay (a 100% badge on the free line tells the right story), unknown ids stay (fail
  // visible, never hide what we cannot classify), and non-DBM badges are never touched.
  // NOTE: the title regex is duplicated in the sibling helpers on purpose — each helper is
  // extraction-executed standalone by tests/dbm-vouchers.test.mjs.
  function shouldSuppressLineBadge(title, typeMap) {
    var m = /^dbm-vouchers?-(.+)$/.exec(String(title || ''));
    if (!m) { return false; }
    var ids = m[1].split('+').filter(function (id) { return !!id; });
    if (!ids.length) { return false; }
    return ids.every(function (id) {
      var t = typeMap[String(id)];
      return t === 'amount' || t === 'percent';
    });
  }

  // The exact voucher savings, in cents, summed from Shopify's own allocations (cart-level
  // applications + line-level allocations) — always the truth, even when the middleware
  // clamped or merged (`dbm-vouchers-<a>+<b>`) the discounts. Mirrors the suppression rule:
  // an allocation counts only when EVERY id in its title is a known value voucher, so the
  // summary total accounts for exactly the badges that were hidden.
  function voucherSavingsCents(cart, typeMap) {
    function allValue(title) {
      var m = /^dbm-vouchers?-(.+)$/.exec(String(title || ''));
      if (!m) { return false; }
      var ids = m[1].split('+').filter(function (id) { return !!id; });
      if (!ids.length) { return false; }
      return ids.every(function (id) {
        var t = typeMap[String(id)];
        return t === 'amount' || t === 'percent';
      });
    }
    var total = 0;
    var apps = cart && cart.cart_level_discount_applications;
    if (Array.isArray(apps)) {
      apps.forEach(function (a) {
        if (a && allValue(a.title)) { total += Number(a.total_allocated_amount) || 0; }
      });
    }
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      var allocs = item && item.line_level_discount_allocations;
      if (Array.isArray(allocs)) {
        allocs.forEach(function (a) {
          if (a && a.discount_application && allValue(a.discount_application.title)) { total += Number(a.amount) || 0; }
        });
      }
    });
    return total;
  }

  // Renders cents through a Shopify money_format string (the theme's own formatter,
  // exposed as themeVariables.settings.moneyFormat by snippets/js-variables.liquid).
  function formatMoneyCents(cents, format) {
    var n = Number(cents);
    if (!isFinite(n)) { return ''; }
    function render(amount, decimals, thousands, decimalSep) {
      var parts = amount.toFixed(decimals).split('.');
      var out = thousands ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) : parts[0];
      if (decimals > 0) { out += decimalSep + parts[1]; }
      return out;
    }
    var amount = Math.abs(n) / 100;
    var fmt = (typeof format === 'string' && format) ? format : 'R{{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      if (key === 'amount_no_decimals') { return render(amount, 0, ',', '.'); }
      if (key === 'amount_with_comma_separator') { return render(amount, 2, '.', ','); }
      if (key === 'amount_no_decimals_with_comma_separator') { return render(amount, 0, '.', ','); }
      if (key === 'amount_with_space_separator') { return render(amount, 2, ' ', ','); }
      if (key === 'amount_no_decimals_with_space_separator') { return render(amount, 0, ' ', ','); }
      if (key === 'amount_with_apostrophe_separator') { return render(amount, 2, "'", '.'); }
      return render(amount, 2, ',', '.'); // amount + any unknown placeholder
    }).replace(/<[^>]*>/g, '').trim();
  }

  function themeMoney(cents) {
    var format = null;
    try {
      format = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat;
    } catch (e) {}
    return formatMoneyCents(cents, format);
  }

  // The NOP-style per-voucher label: the voucher's NOMINAL benefit from /available.
  // The summary TOTAL row is computed from actual allocations instead — it is the truth.
  function nominalOff(v) {
    if (v.type === 'percent' && v.value != null) { return Math.round(Number(v.value) * 100) + '% off'; }
    if (v.type === 'amount' && v.value != null) { return themeMoney(Math.round(Number(v.value) * 100)) + ' off'; }
    return '';
  }

  function money(v) { var n = Number(v); return isFinite(n) ? 'R' + n.toFixed(2) : ''; }

  function rewardLabel(v) {
    if (v.type === 'percent' && v.value != null) { return Math.round(Number(v.value) * 100) + '% off'; }
    if (v.type === 'amount' && v.value != null) { return money(v.value) + ' off'; }
    if (v.type === 'free_shipping') { return 'Free shipping'; }
    if (v.type === 'free_product') { return 'Free product'; }
    return '';
  }

  function fmtDate(iso) {
    try { var d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString(); }
    catch (e) { return ''; }
  }

  function setState(container, state, message) {
    var status = container.querySelector('[data-dbm-vouchers-status]');
    var list = container.querySelector('[data-dbm-vouchers-list]');
    var retry = container.querySelector('[data-dbm-vouchers-retry]');
    var confirm = container.querySelector('[data-dbm-vouchers-confirm]');
    var summary = container.querySelector('[data-dbm-vouchers-summary]');
    if (status) { status.textContent = message || ''; status.hidden = !message; }
    if (list) { list.hidden = state !== 'available' && state !== 'applied' && state !== 'inapplicable'; }
    if (retry) { retry.hidden = state !== 'retryable-error'; }
    if (confirm && state !== 'confirm') { confirm.hidden = true; }
    if (summary && state !== 'available' && state !== 'applied' && state !== 'inapplicable') { summary.hidden = true; summary.innerHTML = ''; }
    container.setAttribute('data-dbm-vouchers-state', state);
  }

  function setAllState(state, message) {
    roots().forEach(function (c) { setState(c, state, message); });
  }

  function clearErrors() {
    roots().forEach(function (c) {
      var status = c.querySelector('[data-dbm-vouchers-status]');
      if (status) { status.textContent = ''; status.hidden = true; }
    });
  }

  function showError(container, msg) {
    if (!container) { return; }
    setState(container, 'retryable-error', msg);
  }

  function showInapplicable(container, msg) {
    if (!container) { return; }
    setState(container, 'inapplicable', msg);
  }

  function voucherCodeSet() {
    var set = {};
    var rows = lastData && Array.isArray(lastData.vouchers) ? lastData.vouchers : [];
    rows.forEach(function (v) { if (v.code) { set[String(v.code).toLowerCase()] = true; } });
    return set;
  }

  function nonVoucherCodes(cart) {
    var known = voucherCodeSet();
    return (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []).filter(function (d) {
      var code = d && d.code ? String(d.code).toLowerCase() : '';
      return code && !known[code] && !/^dbmv-/i.test(code);
    });
  }

  function cartRewardLine(cart, voucherId) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    return items.find(function (item) {
      return item && item.properties && String(item.properties._dbm_voucher_id || '') === String(voucherId);
    });
  }

  function ensureFreeProduct(voucher, container, cart) {
    if (!voucher || voucher.type !== 'free_product') { return Promise.resolve(cart); }
    if (cartRewardLine(cart, voucher.voucher_id)) { return Promise.resolve(cart); }
    var existing = (cart && Array.isArray(cart.items) ? cart.items : []).find(function (item) {
      return item && voucher.product_sku && String(item.sku || '').toLowerCase() === String(voucher.product_sku).toLowerCase();
    });
    if (existing) { return Promise.resolve(cart); }
    if (!voucher.variant_id) {
      showError(container, 'This free-product reward is not ready to add automatically. Please try again later.');
      return Promise.reject(new Error('free_product_variant_missing'));
    }
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(voucher.variant_id), quantity: 1, properties: { _dbm_voucher_id: String(voucher.voucher_id) } }] })
    }).then(function (r) {
      if (!r.ok) { throw new Error('free_product_add_failed'); }
      return fetchCart();
    }).then(function (nextCart) {
      if (!nextCart || !cartRewardLine(nextCart, voucher.voucher_id)) {
        throw new Error('free_product_add_unconfirmed');
      }
      return nextCart;
    }).catch(function (error) {
      showError(container, error.message === 'free_product_add_failed'
        ? 'The free-product reward could not be added to your cart.'
        : 'The free-product reward could not be confirmed in your cart.');
      throw error;
    });
  }

  function removeOwnedRewards(cart, applied) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var active = applied && String(applied).toLowerCase();
    var work = Promise.resolve();
    items.forEach(function (item) {
      var owner = item && item.properties && item.properties._dbm_voucher_id;
      var ownerVoucher = lastData && lastData.vouchers && lastData.vouchers.find(function (v) { return String(v.voucher_id) === String(owner); });
      if (owner && (!ownerVoucher || !active || !ownerVoucher.code || String(ownerVoucher.code).toLowerCase() !== active)) {
        work = work.then(function () {
          return fetch(cartRoot() + 'cart/change.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 })
          });
        });
      }
    });
    return work;
  }

  // Apply EXACTLY [code] to the cart via the theme's mechanism, then repaint after a fresh cart read.
  function applyCode(code, container, voucher) {
    if (!code) { return; }
    var minSpend = voucher && voucher.min_spend;
    ensureFreeProduct(voucher, container, lastCart).then(function () {
    fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }) // REPLACE — only the chosen voucher code
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) { showError(container, 'Could not apply the voucher — please try again.'); return; }
        return fetchCart().then(function (freshCart) { return freshCart || cart; });
      }).then(function (cart) {
        if (!cart) { return; }
        lastCart = cart;
        var applied = (cart.discount_codes || []).some(function (d) {
          return d && String(d.code).toLowerCase() === String(code).toLowerCase() && d.applicable;
        });
        if (!applied) {
          // Below minimum spend / not applicable — surface gently, leave the list up.
          showInapplicable(container, minSpend ? ('Total RSP of ' + money(minSpend) + '+ needed to use this voucher.')
            : "This voucher can't be applied to your current cart.");
          return;
        }
        return removeOwnedRewards(cart, code).then(function () {
          if (isCartPage()) { window.location.reload(); return; } // matches the theme's own cart-page behaviour
          try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
          renderAll(cart);
        });
      })
      .catch(function (error) {
        if (error && error.message === 'free_product_variant_missing') { return; }
        showError(container, 'Could not apply the voucher — please try again.');
      });
    }).catch(function () {});
  }

  // POST /apply for a fresh server-side validation (still held + in-window), then apply the returned code.
  function applyVoucher(voucher, container) {
    clearErrors();
    fetch(APPLY, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ voucher_id: voucher.voucher_id })
    }).then(function (r) {
      if (r.ok) { return r.json().then(function (j) { applyCode(j && j.code ? j.code : voucher.code, container, voucher); }); }
      if (r.status === 409) { showError(container, 'This voucher is not available on your account.'); return; }
      if (r.status === 410) { showError(container, 'This voucher has expired.'); return; }
      showError(container, 'The voucher service is temporarily unavailable. Please try again.');
    }).catch(function () { showError(container, 'The voucher service is temporarily unavailable. Please try again.'); });
  }

  function selectVoucher(voucher, container) {
    var conflicts = nonVoucherCodes(lastCart);
    if (conflicts.length) {
      pendingSelection = { voucher: voucher, container: container };
      var confirm = container.querySelector('[data-dbm-vouchers-confirm]');
      var text = container.querySelector('[data-dbm-vouchers-confirm-text]');
      if (text) { text.textContent = 'Applying this voucher will replace your current discount (' + conflicts.map(function (d) { return d.code; }).join(', ') + ').'; }
      if (confirm) { confirm.hidden = false; }
      setState(container, 'confirm', '');
      return;
    }
    applyVoucher(voucher, container);
  }

  function buildRow(v, isApplied, container) {
    var id = 'dbm-voucher-' + container.getAttribute('data-dbm-scope') + '-' + v.voucher_id;
    var row = document.createElement('label');
    row.className = 'dbm-vouchers__row';
    row.setAttribute('for', id);

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = 'dbm-voucher-' + container.getAttribute('data-dbm-scope');
    input.id = id;
    input.value = String(v.voucher_id);
    input.checked = !!isApplied;
    input.addEventListener('change', function () { selectVoucher(v, container); });

    var text = document.createElement('span');
    text.className = 'dbm-vouchers__row-text';
    var main = [];
    if (v.label) { main.push(v.label); }
    var reward = rewardLabel(v);
    if (reward) { main.push(reward); }
    text.appendChild(document.createTextNode(main.join(' · ')));

    var subParts = [];
    if (v.min_spend) { subParts.push('Min ' + money(v.min_spend)); }
    var exp = fmtDate(v.expires_at);
    if (exp) { subParts.push('Expires ' + exp); }
    if (subParts.length) {
      var sub = document.createElement('span');
      sub.className = 'dbm-vouchers__row-sub';
      sub.textContent = subParts.join(' · ');
      text.appendChild(sub);
    }

    row.appendChild(input);
    row.appendChild(text);
    return row;
  }

  function renderInto(container, vouchers, cart) {
    var list = container.querySelector('[data-dbm-vouchers-list]');
    if (!list || !vouchers.length) { setState(container, 'empty', 'You have no active vouchers right now.'); return; }
    var applied = appliedCodes(cart);
    list.setAttribute('role', 'radiogroup');
    list.innerHTML = '';
    vouchers.forEach(function (v) {
      list.appendChild(buildRow(v, v.code && applied[String(v.code).toLowerCase()], container));
    });
    var hasApplied = vouchers.some(function (v) { return v.code && applied[String(v.code).toLowerCase()]; });
    setState(container, hasApplied ? 'applied' : 'available', hasApplied ? 'A voucher is applied to this cart.' : 'Choose a voucher to apply.');
    var clear = container.querySelector('[data-dbm-vouchers-clear]');
    if (clear) { clear.hidden = true; } // legacy codes mode: the radio owns the slot, no clear-all
    var summary = container.querySelector('[data-dbm-vouchers-summary]');
    if (summary) { summary.hidden = true; summary.innerHTML = ''; } // summary rows are a function-mode surface
  }

  function renderAll(cart) {
    var vouchers = (lastData && Array.isArray(lastData.vouchers)) ? lastData.vouchers : [];
    if (!vouchers.length) { roots().forEach(function (c) { setState(c, 'empty', 'You have no active vouchers right now.'); }); return; }
    roots().forEach(function (c) { renderInto(c, vouchers, cart); });
  }

  function maybeAutoApply(data, cart) {
    if (!(data.auto_apply && data.vouchers.length === 1)) { return; }
    if (!cart || !cart.item_count) { return; } // nothing to discount yet
    var only = data.vouchers[0];
    var applied = appliedCodes(cart);
    if (only.code && applied[String(only.code).toLowerCase()]) { return; } // already applied
    var didAuto = false;
    try { didAuto = sessionStorage.getItem(AUTO_KEY) === String(only.voucher_id); } catch (e) {}
    if (didAuto) { return; }
    try { sessionStorage.setItem(AUTO_KEY, String(only.voucher_id)); } catch (e) {}
    applyCode(data.auto_apply, roots()[0], only);
  }

  // ───────────────────────── v0.3 multi-select (function mode) ─────────────────────────

  // annique-issues#217 — the ids that are BOTH selected and actually ON THE LIST. Everything the
  // consultant is told about her selection reads from this, so a tick she cannot see can never be
  // counted for her. The BFF already filters `selected[]` to the offered set with one predicate;
  // this is the client leg of the same rule, not a second opinion about eligibility.
  function selectedOnList(selectedIds, vouchers) {
    var offered = {};
    (Array.isArray(vouchers) ? vouchers : []).forEach(function (v) {
      if (v && v.voucher_id != null) { offered[String(v.voucher_id)] = true; }
    });
    return (Array.isArray(selectedIds) ? selectedIds : []).filter(function (id) { return offered[String(id)] === true; });
  }

  // annique-issues#217 — the count must agree with the list. Reported live: "1 of 2 vouchers
  // selected" beside a SINGLE row (consultant 539510 held v64 "Buy for R4999 and get Free NAD",
  // voucher PAUSED, plus v65 "Free Shipping", ACTIVE). Two things were wrong with the sentence:
  //
  //   · the DENOMINATOR was the raw cap. `max_per_order` is a LIMIT, not a count of what she has,
  //     so a consultant with ONE offered voucher under a cap of 2 was told about a voucher she can
  //     neither see nor use, on the same screen. Bounding it by what is actually offered
  //     (min(cap, offered)) makes the same number honest as both.
  //   · the NUMERATOR was lastSelected.length unfiltered, so an id absent from the rendered list
  //     would count a tick that is nowhere on screen.
  //
  // The paused voucher itself is filtered server-side, and correctly so — the list is right and
  // stays right. ⛔ Do not "fix" this by listing paused vouchers.
  // The denominator. Extracted (#218 round 3) so the SENTENCE and the TICKS cannot drift apart:
  // both read this one number. Never promise more slots than there are vouchers to fill them with.
  // (offered 0 => the empty state owns the surface and this is unreachable; fall back to the cap
  // rather than render "0".)
  function statusLimit(offeredCount, cap) {
    return Math.min(cap, Number(offeredCount) || 0) || cap;
  }

  // annique-issues#218 round 3 — WHICH TICKS ARE HONOURED. Round 2 made the cap-shrink SENTENCE
  // honest ("Only 2 of your 3 selected vouchers can be used on one order") and left all three rows
  // rendering a plain tick, so one screen said two different things.
  //
  // The Function slices the selection to the cap IN SELECTION ORDER, so the surface names the same
  // rows: the first `limit` are honoured, and each of the rest says on its OWN row that it is not
  // being used. ⛔ The excess keeps its CHECKED box on purpose — the sentence tells her to remove
  // one to choose which, and unticking it for her would remove the only control that does that.
  function honouredSet(onListIds, limit) {
    var set = {};
    var n = Math.max(0, Number(limit) || 0);
    (Array.isArray(onListIds) ? onListIds : []).slice(0, n).forEach(function (id) { set[String(id)] = true; });
    return set;
  }

  // annique-issues#229 — THE ONE HONOURED SET, READ BY BOTH THE SCREEN AND THE CART.
  //
  // ⛔ THIS EXISTS BECAUSE THE PICKER ALREADY COMPUTED THE ANSWER AND THEN DID NOT USE IT FOR THE
  // WRITES. The render path built `honoured` to draw the "Not used on this order" row, while the
  // reward reconcile built its own set straight from `lastSelected` — unfiltered and uncapped. So a
  // consultant with 3 vouchers selected under a cap lowered to 2 was told on screen that the third
  // was not being used, and charged FULL RETAIL for it in the same basket: three cart/add.js calls,
  // three reward lines, one of them with no discount candidate.
  //
  // ⛔ THE FUNCTION IS RIGHT AND IS NOT THE FIX. effective_selection(...).take(cap) in
  // voucher-discounts/src/lib.rs honours exactly `cap`; the storefront was writing lines the
  // discount could never cover. The repair is that the writes read what the screen renders — not a
  // second cap calculation, which is how the two drifted apart in the first place.
  function honouredSelection(selectedIds, vouchers, cap) {
    var list = Array.isArray(vouchers) ? vouchers : [];
    return honouredSet(selectedOnList(selectedIds, list), statusLimit(list.length, cap));
  }

  function multiStatus(selectedOnListIds, offeredCount, cap) {
    var n = (Array.isArray(selectedOnListIds) ? selectedOnListIds : []).length;
    var offered = Number(offeredCount) || 0;
    var limit = statusLimit(offeredCount, cap);
    if (!n) {
      return limit > 1 ? ('Choose up to ' + limit + ' vouchers to use with this order.')
        : 'Choose a voucher to use with this order.';
    }
    // annique-issues#218 — the CAP-SHRINK case. An operator lowering `max_per_order` leaves more
    // ticks standing than the order can carry, and the old arithmetic rendered the impossible
    // "3 of 2 vouchers selected". The Function slices the selection to the cap in selection order,
    // so say exactly that, and say which way out.
    if (n > limit) {
      var excess = n - limit;
      return 'Only ' + limit + ' of your ' + n + ' selected vouchers can be used on one order. Remove '
        + (excess === 1 ? 'one' : excess) + ' to choose which.';
    }
    var base = limit === 1 ? 'Your voucher is selected.' : (n + ' of ' + limit + ' vouchers selected.');
    // Offer the swap only when there IS another voucher to swap to.
    if (n >= limit && offered > n) {
      return base + (limit === 1 ? ' Remove it to choose a different voucher.' : ' Remove one to choose a different voucher.');
    }
    return base;
  }

  // annique-issues#218 round 3 — ONE BASE PER SCREEN. This note used to compare
  // `items_subtotal_price`, which INCLUDES a reward line, against the same floor that
  // `rewardFloorBaseCents` tests with reward lines EXCLUDED. Two bases on one surface: a R210 cart
  // carrying our own R289 reward line read as R499 here and told her the R499 floor was cleared,
  // while the gate that decides the line was simultaneously taking it away. The note now reads the
  // GATE's base, handed in by the caller, so the sentence and the behaviour cannot disagree.
  //
  // `notUsed` is the cap-shrink row: her tick stands, but the Function will not honour it.
  function rowNote(v, isSelected, appliedSet, baseCents, notUsed, limit) {
    if (!isSelected) { return ''; }
    if (notUsed) {
      return 'Not used on this order — only ' + limit + (limit === 1 ? ' voucher can' : ' vouchers can')
        + ' be used at a time. Remove another to use this one.';
    }
    if (appliedSet[String(v.voucher_id)]) { return ''; }
    // annique-issues#401 — the FLOOR comes first, for every type. This used to return the
    // free_shipping promise before it looked at min_spend, so 29 October buyers of voucher 106
    // (floor R665, bases R0-R625.65) were told "Free shipping is applied at checkout." and then paid
    // shipping: the Function refuses below the floor (lib.rs `base_cents < entry.min_spend_cents`).
    var min = Number(v.min_spend);
    var base = Number(baseCents);
    if (isFinite(min) && min > 0 && isFinite(base) && base < min * 100) {
      return 'Total RSP of ' + themeMoney(Math.round(min * 100)) + '+ needed to use this voucher.';
    }
    if (v.type === 'free_shipping') { return 'Free shipping is applied at checkout.'; }
    return '';
  }

  function buildMultiRow(v, opts, container) {
    var id = 'dbm-voucher-' + container.getAttribute('data-dbm-scope') + '-' + v.voucher_id;
    var row = document.createElement('label');
    row.className = 'dbm-vouchers__row' + (opts.notUsed ? ' dbm-vouchers__row--not-used' : '');
    row.setAttribute('for', id);

    var input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.value = String(v.voucher_id);
    input.checked = !!opts.checked;
    input.disabled = !!opts.disabled;
    input.addEventListener('change', function () { toggleVoucher(v, input, container); });

    var text = document.createElement('span');
    text.className = 'dbm-vouchers__row-text';
    var main = [];
    if (v.label) { main.push(v.label); }
    var reward = rewardLabel(v);
    if (reward) { main.push(reward); }
    text.appendChild(document.createTextNode(main.join(' · ')));
    if (opts.applied) {
      var badge = document.createElement('span');
      badge.className = 'dbm-vouchers__row-applied';
      badge.textContent = 'Applied';
      text.appendChild(badge);
    }

    var subParts = [];
    if (v.min_spend) { subParts.push('Min ' + money(v.min_spend)); }
    var exp = fmtDate(v.expires_at);
    if (exp) { subParts.push('Expires ' + exp); }
    if (subParts.length) {
      var sub = document.createElement('span');
      sub.className = 'dbm-vouchers__row-sub';
      sub.textContent = subParts.join(' · ');
      text.appendChild(sub);
    }
    if (opts.note) {
      var note = document.createElement('span');
      note.className = 'dbm-vouchers__row-sub dbm-vouchers__row-note';
      note.textContent = opts.note;
      text.appendChild(note);
    }

    row.appendChild(input);
    row.appendChild(text);
    return row;
  }

  function renderMultiInto(container, notice) {
    var list = container.querySelector('[data-dbm-vouchers-list]');
    if (!list) { return; }
    var cap = capOf(lastData);
    var applied = appliedVoucherIds(lastCart);
    // #217 — one filtered set drives the ticks, the count, the state and the at-cap disable.
    var onList = selectedOnList(lastSelected, lastData.vouchers);
    var selectedSet = {};
    onList.forEach(function (id) { selectedSet[String(id)] = true; });
    var atCap = onList.length >= cap;
    // #218 round 3 — the SAME limit the sentence uses, the SAME base the reward gate uses.
    var limit = statusLimit(lastData.vouchers.length, cap);
    // #229 — the SAME call the cart writes make. Two call sites, one function, no drift.
    var honoured = honouredSelection(lastSelected, lastData.vouchers, cap);
    var floorBase = rewardFloorBaseCents(lastCart);
    list.setAttribute('role', 'group');
    list.innerHTML = '';
    lastData.vouchers.forEach(function (v) {
      var isSel = !!selectedSet[String(v.voucher_id)];
      var notUsed = isSel && !honoured[String(v.voucher_id)];
      list.appendChild(buildMultiRow(v, {
        checked: isSel,
        disabled: multiBusy || (!isSel && atCap),
        applied: !!applied[String(v.voucher_id)],
        notUsed: notUsed,
        note: rowNote(v, isSel, applied, floorBase, notUsed, limit)
      }, container));
    });
    var clear = container.querySelector('[data-dbm-vouchers-clear]');
    // Deliberately the RAW selection: if the server still holds rows we cannot show her, the escape
    // hatch that clears them (/select []) must not disappear along with the rows.
    if (clear) { clear.hidden = multiBusy || !lastSelected.length; }
    setState(
      container,
      onList.length ? 'applied' : 'available',
      notice || multiStatus(onList, lastData.vouchers.length, cap));
    renderSummary(container); // after setState — setState clears the summary for non-list states
  }

  // NOP-style summary rows under the picker: one label row per APPLIED value voucher
  // (name + nominal benefit from /available) + a total row summed from the actual
  // allocations. When a floor clamp or merge makes the actual differ from the nominal
  // sum, the total row wins — it is the truth; the per-voucher rows are labels.
  function renderSummary(container) {
    var summary = container.querySelector('[data-dbm-vouchers-summary]');
    if (!summary) { return; }
    summary.innerHTML = '';
    summary.hidden = true;
    if (!lastData || !lastCart) { return; }
    var typeMap = voucherTypeMap(lastData.vouchers);
    var applied = appliedVoucherIds(lastCart);
    var appliedValue = lastData.vouchers.filter(function (v) {
      var t = typeMap[String(v.voucher_id)];
      return !!applied[String(v.voucher_id)] && (t === 'amount' || t === 'percent');
    });
    var savings = voucherSavingsCents(lastCart, typeMap);
    if (!appliedValue.length || savings <= 0) { return; }
    appliedValue.forEach(function (v) {
      var row = document.createElement('div');
      row.className = 'dbm-vouchers__summary-row';
      var name = document.createElement('span');
      name.textContent = v.label || 'Voucher';
      var off = document.createElement('span');
      off.textContent = nominalOff(v);
      row.appendChild(name);
      row.appendChild(off);
      summary.appendChild(row);
    });
    var total = document.createElement('div');
    total.className = 'dbm-vouchers__summary-row dbm-vouchers__summary-total';
    var totalLabel = document.createElement('span');
    totalLabel.textContent = 'Voucher savings';
    var totalValue = document.createElement('span');
    totalValue.textContent = '−' + themeMoney(savings);
    total.appendChild(totalLabel);
    total.appendChild(totalValue);
    summary.appendChild(total);
    summary.hidden = false;
  }

  // Hide the per-line discount badges that belong to VALUE vouchers (classified via
  // data-dbm-line-discount, stamped by snippets/line-item.liquid). Runs on every multi-mode
  // render so a re-rendered drawer/page is re-classified; free_product and non-DBM badges
  // keep their display untouched.
  function suppressValueLineBadges() {
    if (!lastData || !isMultiMode(lastData)) { return; }
    var typeMap = voucherTypeMap(lastData.vouchers);
    Array.prototype.slice.call(document.querySelectorAll('[data-dbm-line-discount]')).forEach(function (el) {
      var title = el.getAttribute('data-dbm-line-discount');
      el.style.display = shouldSuppressLineBadge(title, typeMap) ? 'none' : '';
    });
  }

  function renderMultiAll(notice) {
    if (!lastData) { return; }
    var text = notice || takeNotice(); // #218 r3 — an explanation that outlived a reload
    roots().forEach(function (c) { renderMultiInto(c, text); });
    suppressValueLineBadges();
  }

  function setMultiBusyUi() {
    roots().forEach(function (c) {
      Array.prototype.slice.call(c.querySelectorAll('[data-dbm-vouchers-list] input')).forEach(function (i) { i.disabled = true; });
      var clear = c.querySelector('[data-dbm-vouchers-clear]');
      if (clear) { clear.hidden = true; }
      var status = c.querySelector('[data-dbm-vouchers-status]');
      if (status) { status.textContent = 'Updating your vouchers…'; status.hidden = false; }
    });
  }

  // The checkbox was toggled optimistically by the browser; validate the new set and submit it.
  function toggleVoucher(v, input, container) {
    if (multiBusy) { input.checked = !input.checked; return; }
    // annique-issues#218 — the SAME filtered set that drives the ticks, the count and the at-cap
    // disable (#217). Reading the RAW selection here was the same lie one layer down: a selection
    // row we cannot show her still occupied a cap slot, so the surface said "1 of 2" with a free
    // checkbox and her click came back "You can use up to 2 vouchers for one order."
    // Submitting the filtered set also retires that dead row, which the Function counts against
    // her cap until the sweep clears it. The clear-all escape hatch still keys off the RAW set.
    var onList = selectedOnList(lastSelected, lastData && lastData.vouchers);
    var result = nextSelection(onList, v.voucher_id, input.checked, capOf(lastData));
    if (result.error === 'cap') {
      input.checked = false; // defensive — the input is disabled at cap
      showInapplicable(container, 'You can use up to ' + capOf(lastData) + ' vouchers for one order.');
      return;
    }
    submitSelection(result.ids, container);
  }

  // ───────── annique-issues#218: the restore/tick race, closed in BOTH directions ─────────
  //
  // `restoreBusy` was one-directional. It stopped a restore STARTING while a tick owned the cart,
  // and stopped nothing in the other direction: a tick or a clear-all landing during an IN-FLIGHT
  // restore left the restore's `cart/update.js` free to land LAST and put back a voucher she had
  // just removed. Two rules make it symmetric, and the priority in both is the same — THE USER'S
  // ACTION WINS, because it is the only one of the two she made on purpose.
  //
  //   1. ORDER — a user write queues behind an in-flight restore, so hers is the last write.
  //   2. OUTCOME — a restore whose epoch moved while it was in flight abandons its result: it does
  //      not re-assert the attribute, it does not re-render, and it does not reload.
  //
  // Both are pure and extractable on purpose: this is the exact trap the design set out to avoid,
  // so it is proven by RUNNING an interleaving, never by reading the source.

  // Rule 1. Run `fn` after the in-flight restore settles (either way — a failed restore must not
  // strand her tick for ever). No restore in flight ⇒ a resolved gate, i.e. the next microtask.
  function afterRestore(gate, fn) {
    return (gate || Promise.resolve()).then(fn, fn);
  }

  // Rule 1, widened to EVERY background writer (#218 round 3). Round 2 queued a user write behind
  // the RESTORE alone, and the load-path reconcile is a SECOND writer on the same cart — so a
  // reconcile already on the wire could still land after her tick. Every live gate is neutralised
  // (a failed one must never strand her) and then joined, so hers is the last write, full stop.
  function afterGates(gates, fn) {
    var live = (Array.isArray(gates) ? gates : []).filter(Boolean);
    if (!live.length) { return afterRestore(null, fn); }
    return Promise.all(live.map(function (g) {
      return g.then(null, function () { return null; });
    })).then(fn);
  }

  // Rule 1b. Has a user selection begun since this restore started?
  function restoreSuperseded(startEpoch) {
    return selectionEpoch !== startEpoch;
  }

  // annique-issues#218 — DID THE WRITE LAND? Extracted and executed by the suite, because the
  // previous proof of this was a regex for `var landed = ` plus a regex for the `if (landed && …)`
  // line. Both keep matching while the predicate itself is replaced by `true`, so the suite stayed
  // green through a mutation that removes the whole reload guard. The predicate is now a named
  // function the tests CALL, and a control asserts a mismatched value is not "landed".
  //
  // Strict equality against the re-read cart, and `|| ''` on the read side only: an attribute the
  // cart does not carry reads as '' and must never equal a real value.
  function restoreLanded(cart, attr) {
    if (!cart || !attr || !attr.key) { return false; }
    var attrs = cart.attributes;
    if (!attrs) { return false; }
    return String(attrs[attr.key] || '') === String(attr.value);
  }

  // Rule 2. What a finished restore may still do.
  //   superseded — a user selection began after this restore started: ABANDON, unconditionally and
  //                before anything else is considered. She removed it; it stays removed.
  //   landed     — the re-read cart really carries the value we wrote. ⛔ A reload may only follow
  //                a write that landed, once (a reload restarts the page view; an unlanded write
  //                would reload for ever — the sessionStorage stamp is the second stop on the trap).
  function restoreOutcome(landed, superseded, failed, onCartPage, alreadyReloaded) {
    if (superseded) { return 'abandon'; }
    if (landed && onCartPage && !failed && !alreadyReloaded) { return 'reload'; }
    return 'render';
  }

  // Reconcile the cart's reward lines with the server's ensure_lines:
  //   remove owned (`_dbm_voucher_id`-marked) lines whose voucher was deselected or has dropped
  //   below its floor — NEVER customer lines;
  //   add each missing free-product variant WHOSE VOUCHER'S FLOOR THE CART CLEARS. An add failure
  //   (e.g. sold out) is soft: the selection stands and a notice is shown, because the attribute is
  //   already the source of truth.
  //
  // annique-issues#218 — the floor gate lives HERE, in the ONE path both the tick and the load-path
  // restore go through, because both of them added a full-price reward line below the floor. The
  // restore was the loud case (no user action at all), but the tick charged her just the same.
  // PURE. Everything this reconcile WOULD do to this cart, as a list of ops with stable ids.
  // Extracted (#218 round 3) because the load path has to answer "is there anything to do?" without
  // spending a request, and the answer has to be the SAME one the writer then acts on.
  function planRewardOps(cart, ensureLines, vouchers, selectedSet) {
    // annique-issues#229 — CAP THE ADDS. `selectedSet` is the HONOURED set (see honouredSelection),
    // so an ensure_line for a voucher the cap excludes never reaches linesToAdd and never becomes a
    // full-price line she was told she is not getting. Filtering here rather than at the two call
    // sites means the tick path and the load path are both covered by one guard.
    var honouredEnsure = (Array.isArray(ensureLines) ? ensureLines : []).filter(function (line) {
      return !!(line && line.voucher_id != null && selectedSet[String(line.voucher_id)] === true);
    });
    var verdict = classifyEnsureLines(cart, honouredEnsure, vouchers);
    var ops = [];
    linesToRemove(cart, selectedSet, verdict.below).forEach(function (item) {
      ops.push({
        kind: 'remove', id: 'remove:' + String(item.key), key: item.key,
        voucher_id: String(item.properties._dbm_voucher_id)
      });
    });
    linesToAdd(cart, verdict.add).forEach(function (line) {
      // #218 round 3 — she DELETED this gift from this cart. It does not come back on its own.
      if (stamped(REFUSED_KEY, stampKey(cart, line.voucher_id))) { return; }
      ops.push({
        kind: 'add', id: 'add:' + String(line.voucher_id) + ':' + String(line.variant_id), line: line,
        voucher_id: String(line.voucher_id)
      });
    });
    return ops;
  }

  // ─────────── annique-issues#218 round 3 (S1): THE ATTEMPT LEDGER ───────────
  //
  // An op is issued AT MOST ONCE per page view. This is one of the two stops on the request storm
  // and it is the one that holds when the other cannot:
  //   · a REFUSED add (sold out, unpublished, per-order limit met, stale variant after a PIM
  //     re-import, stale item.key) leaves `linesToAdd` non-empty for ever, so every re-entry
  //     recomputes the same work and issues it again;
  //   · a 200 the re-read cart does not actually reflect (the variant lands under another id)
  //     would loop even with the landed-gate on the dispatch, because work really did "land".
  // MEASURED before this: adds 15 / 50 / 150 against fetch caps of 60 / 200 / 600, ~4 requests per
  // turn, one of them the DBM app proxy. A reload is a fresh page view and tries once more; any
  // USER action clears the ledger outright (submitSelection), because she asked for it.
  function unattemptedOps(ops) {
    return (Array.isArray(ops) ? ops : []).filter(function (op) { return rewardOpAttempts[op.id] !== true; });
  }

  function claimRewardOps(ops) {
    (Array.isArray(ops) ? ops : []).forEach(function (op) { rewardOpAttempts[op.id] = true; });
    return ops;
  }

  function resetRewardOpAttempts() {
    rewardOpAttempts = {};
  }

  // #218 S1 — SHE MUST BE TOLD. A gift that cannot be added and a full-price line that cannot be
  // taken away both cost her money, and the removal leg was doubly bad: it never checked `r.ok`,
  // so it failed SILENTLY and looped. Each sentence names the money and the action open to her.
  function reconcileNotice(addFailed, removeFailed) {
    if (addFailed && removeFailed) {
      return 'We could not update your cart for your vouchers. Please refresh the page, and check your cart before you pay.';
    }
    if (addFailed) {
      return 'Your voucher is selected, but the free product could not be added to your cart. Please try again.';
    }
    if (removeFailed) {
      return 'A free-product line that no longer applies is still in your cart and we could not remove it. Please remove it yourself — you would be charged full price for it.';
    }
    return null;
  }

  function postRewardOp(op) {
    if (op.kind === 'remove') {
      return fetch(cartRoot() + 'cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: op.key, quantity: 0 })
      });
    }
    return fetch(cartRoot() + 'cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(op.line.variant_id), quantity: Number(op.line.qty) || 1, properties: { _dbm_voucher_id: String(op.line.voucher_id) } }] })
    });
  }

  // ⛔ `r.ok` on BOTH legs. The removal leg never checked it, so a refused removal set no flag, showed
  // no notice, and came back round on the next dispatch — silent AND looping, the worst pair.
  function recordRewardOp(op, ok, outcome, cart) {
    if (op.kind === 'remove') {
      if (!ok) { outcome.removeFailed = true; rewardRemoveFailed = true; return; }
      outcome.removed += 1;
      // WE took this line away. Forget we ever saw it, or the next time the floor clears its absence
      // would read as HER deleting it and we would deselect a voucher she never refused.
      stampDrop(SEEN_KEY, stampKey(cart, op.voucher_id));
      return;
    }
    if (!ok) { outcome.addFailed = true; rewardAddFailed = true; return; }
    outcome.added += 1;
  }

  function reconcileRewardLines(cart, ensureLines, vouchers, abandoned) {
    // #229 — the HONOURED set, not the raw selection. linesToRemove reads this same set, so a line
    // the cap now excludes is taken back OUT of the cart, not merely never added again. Fixing only
    // the adds would leave her cart wrong in the other direction, which is harder to spot.
    var selectedSet = honouredSelection(
      lastSelected, vouchers || (lastData && lastData.vouchers), capOf(lastData));
    var ops = claimRewardOps(unattemptedOps(
      planRewardOps(cart, ensureLines, vouchers || (lastData && lastData.vouchers), selectedSet)));
    var outcome = { added: 0, removed: 0, addFailed: false, removeFailed: false, abandoned: false };
    var work = Promise.resolve();
    ops.forEach(function (op) {
      work = work.then(function () {
        // Rule 2 of the race, applied PER OP: she acted, so stop writing to her cart mid-chain.
        if (abandoned && abandoned()) { outcome.abandoned = true; return null; }
        return postRewardOp(op).then(function (r) {
          recordRewardOp(op, !!(r && r.ok), outcome, cart);
        }, function () {
          recordRewardOp(op, false, outcome, cart);
        });
      });
    });
    return work.then(function () { return outcome; });
  }

  // The /select round-trip succeeded: write the cart attribute EXACTLY as the server returned it
  // (never client-composed), reconcile reward lines, re-read the cart, then re-render from Shopify.
  function applySelection(resp) {
    lastSelected = Array.isArray(resp.selected) ? resp.selected.slice() : [];
    var attrs = {};
    attrs[resp.cart_attribute.key] = resp.cart_attribute.value;
    return fetch(cartRoot() + 'cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (!r.ok) { throw new Error('attribute_write_failed'); }
      return r.json().catch(function () { return null; });
    }).then(function (cart) {
      return reconcileRewardLines(cart || lastCart, resp.ensure_lines);
    }).then(function () {
      return fetchCart();
    }).then(function (cart) {
      if (cart) { lastCart = cart; }
      if (isCartPage() && !rewardAddFailed && !rewardRemoveFailed) { window.location.reload(); return { action: 'none' }; }
      try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
      return { action: 'render' };
    });
  }

  // Replace the whole selection set server-side (empty array = clear all).
  function submitSelection(ids, container) {
    multiBusy = true;
    clearErrors();
    setMultiBusyUi();
    // #218 — this is a USER action. Bump the epoch so an in-flight restore stands down, and QUEUE
    // behind it so her value is the last one written to the cart.
    selectionEpoch += 1;
    // #218 round 3 — she acted, so nothing this page view held against her stands any more.
    // A voucher she is NEWLY ticking also gets its refusal cleared: deleting the gift refused it,
    // ticking it again is her reversing that, and only a NEW tick may do so (the refusal path
    // below submits a strict SUBSET, so it can never clear the refusal it just recorded).
    var alreadySelected = {};
    lastSelected.forEach(function (sel) { alreadySelected[String(sel)] = true; });
    (Array.isArray(ids) ? ids : []).forEach(function (sel) {
      if (!alreadySelected[String(sel)]) { stampDrop(REFUSED_KEY, stampKey(lastCart, sel)); }
    });
    resetRewardOpAttempts();
    afterGates([restoreGate, reconcileGate], function () { return fetch(SELECT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ voucher_ids: ids })
    }); }).then(function (r) {
      if (r.ok) {
        return r.json().catch(function () { return null; }).then(function (resp) {
          if (!resp || !resp.cart_attribute || !resp.cart_attribute.key) { throw new Error('select_bad_response'); }
          return applySelection(resp);
        });
      }
      if (r.status === 401 || r.status === 403) { return { action: 'signed-out' }; }
      if (r.status === 409) { return { action: 'refresh', notice: 'That voucher is not available on your account any more. Your voucher list is now up to date.' }; }
      if (r.status === 410) { return { action: 'refresh', notice: 'That voucher has expired or is no longer available. Your voucher list is now up to date.' }; }
      if (r.status === 422) { return { action: 'notice', notice: 'You can use up to ' + capOf(lastData) + ' vouchers for one order.' }; }
      throw new Error('select_failed');
    }).then(function (outcome) {
      multiBusy = false;
      if (!outcome || outcome.action === 'none') { return; }
      if (outcome.action === 'signed-out') {
        lastData = null;
        setAllState('signed-out', 'Sign in to view and use your vouchers.');
        return;
      }
      if (outcome.action === 'refresh') { return refreshMulti(outcome.notice); }
      if (outcome.action === 'notice') { renderMultiAll(outcome.notice); return; }
      if (outcome.action === 'render') {
        var addFailed = rewardAddFailed;
        var removeFailed = rewardRemoveFailed;
        rewardAddFailed = false;
        rewardRemoveFailed = false;
        renderMultiAll(reconcileNotice(addFailed, removeFailed));
      }
    }).catch(function () {
      multiBusy = false;
      renderMultiAll('The voucher service is temporarily unavailable. Please try again.');
      if (!lastData) { setAllState('retryable-error', 'The voucher service is temporarily unavailable. Please try again.'); }
    });
  }

  // A 409/410 means our list is stale: re-fetch, re-render, then show the polite notice.
  function refreshMulti(notice) {
    return Promise.all([fetchAvailable(), fetchCart()]).then(function (res) {
      var available = res[0], cart = res[1];
      var data = available && available.ok && available.data;
      if (!data || !Array.isArray(data.vouchers)) { run(); return; }
      lastCart = cart || lastCart;
      if (!data.vouchers.length) {
        lastData = null;
        setAllState('empty', notice || 'You have no active vouchers right now.');
        return;
      }
      lastData = data;
      lastSelected = Array.isArray(data.selected) ? data.selected.slice() : [];
      if (!isMultiMode(data)) { renderAll(lastCart); return; }
      renderMultiAll(notice);
    });
  }

  // ─────────────────── annique-issues#216: put a surviving selection back ───────────────────
  //
  // THE DEFECT is a MISSING WRITE on a path with NO user action. The selection cart attribute (whose
  // key only ever arrives in the BFF response) is written in exactly one place — applySelection(),
  // reached only from submitSelection(), which fires only on a tick or an untick. The load path
  // renders the tick from `available`'s server-derived selected[], `voucher_selections` outlives
  // order completion, and a fresh Shopify cart carries no
  // attributes. The read survived the cart and the write did not, so from her second order on the
  // picker said "applied" while she paid full freight (consultant 539510: #480207 [64], #480208
  // [65], #480209 and #480211 absent).
  //
  // THE RULE, and it is the narrowest one that fixes it: write ONLY when the cart carries no value
  // for the key, and only the value the SERVER supplied.
  //   · It cannot LOOP. The write makes its own condition false for that cart, permanently.
  //   · It cannot re-add an UNTICK. An untick goes through /select [], which clears the selection
  //     server-side; `available` then omits cart_attribute entirely, so there is nothing to write.
  //     The non-empty selected[] test below is the belt to that braces.
  //   · It cannot FIGHT the buyer. A cart attribute that DISAGREES with the server is left strictly
  //     alone. A disagreeing value can only come from her own tick in this cart or from a forger,
  //     and overwriting it would silently change what she picked on the strength of a selection made
  //     somewhere else. There is no safety argument for overwriting either: the Function intersects
  //     the attribute with the dbm.fn_vouchers grants metafield, so a forged or stale id already
  //     yields nothing. Writing only into the VOID is also what makes the loop impossible.
  //   · It cannot RESURRECT a stale id. The server derives the value from the same filtered set that
  //     produced the list and the ticks, so a paused / expired / revoked / redeemed / Path-B-held
  //     voucher is absent by construction (annique-issues#217).
  //   · It never runs on an EMPTY cart — the same "nothing to discount yet" bar maybeAutoSelect uses.
  //     Adding the first line re-fires this through cart:change.
  //   · annique-issues#401 — ONE exception to write-into-the-void: a value that names a voucher NOT
  //     on the offered list (see cartSelectionIsStale). That cannot be her tick — she can only tick
  //     what is listed — so replacing it fights nobody, and it terminates: the server's value names
  //     only offered vouchers, so after the write the predicate is false.
  function shouldRestoreSelection(data, cart) {
    var attr = data && data.cart_attribute;
    if (!attr || !attr.key || !attr.value) { return false; }
    if (!Array.isArray(data.selected) || !data.selected.length) { return false; }
    if (!cart || !cart.item_count) { return false; }
    var attrs = cart.attributes;
    var current = attrs && Object.prototype.hasOwnProperty.call(attrs, attr.key) ? attrs[attr.key] : null;
    if (current == null || String(current) === '') { return true; }
    return cartSelectionIsStale(current, data.vouchers, attr.value);
  }

  // annique-issues#401 — a cart attribute OUTLIVES the vouchers it names. A cart created while the
  // September free-shipping vouchers were live kept [90] into October (39 orders), and because the
  // restore only wrote into the void it could never put her LIVE selection on that cart: the picker
  // showed the October voucher ticked while the cart still carried [90].
  // Stale = it names at least one id absent from the offered list, or it is not a parseable list at
  // all (the Function's parse_selection reads that as nothing). Fail-safe both ways: no offered list
  // means no proof, so nothing is stale; "[]" names nothing, so it stays her decision.
  function cartSelectionIsStale(current, vouchers, serverValue) {
    if (!Array.isArray(vouchers) || !vouchers.length) { return false; }
    if (String(current) === String(serverValue)) { return false; }
    var ids;
    try { ids = JSON.parse(String(current)); } catch (e) { return true; }
    if (!Array.isArray(ids)) { return true; }
    var offered = {};
    vouchers.forEach(function (v) { if (v) { offered[String(v.voucher_id)] = true; } });
    return ids.some(function (id) { return !offered[String(id).trim()]; });
  }

  // The one-shot stamp behind the cart-page reload (see below): this cart + this exact value.
  function restoreStamp(cart, value) {
    return String((cart && cart.token) || '') + '|' + String(value);
  }

  function alreadyReloadedFor(cart, value) {
    try { return sessionStorage.getItem(RESTORE_KEY) === restoreStamp(cart, value); } catch (e) { return false; }
  }

  function markReloadedFor(cart, value) {
    try { sessionStorage.setItem(RESTORE_KEY, restoreStamp(cart, value)); } catch (e) {}
  }

  // Write the SERVER's value verbatim, then reconcile the reward lines through the SAME path the
  // tick uses — a free_product voucher lost its free line with the attribute, and restoring one
  // without the other would leave her selected for a product that is not in her basket.
  function restoreSelection(data, cart) {
    var attr = data.cart_attribute;
    var attrs = {};
    attrs[attr.key] = attr.value; // EXACTLY as the server returned it — never composed here.
    restoreBusy = true;
    var startEpoch = selectionEpoch; // #218 — anything she does from here supersedes this restore
    restoreGate = fetch(cartRoot() + 'cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (!r.ok) { throw new Error('attribute_restore_failed'); }
      return r.json().catch(function () { return null; });
    }).then(function (updated) {
      // #218 — she acted while the attribute write was in flight. Stop here: do not touch her
      // reward lines, do not re-read, do not render. Her /select is queued behind this promise.
      if (restoreSuperseded(startEpoch)) { return null; }
      return reconcileRewardLines(updated || cart, data.ensure_lines).then(fetchCart);
    }).then(function (fresh) {
      restoreBusy = false;
      if (fresh) { lastCart = fresh; }
      var landed = restoreLanded(fresh, attr);
      var failed = rewardAddFailed;
      var removeFailed = rewardRemoveFailed;
      rewardAddFailed = false;
      rewardRemoveFailed = false;
      var outcome = restoreOutcome(
        landed, restoreSuperseded(startEpoch), failed || removeFailed, isCartPage(), alreadyReloadedFor(fresh, attr.value));
      if (outcome === 'abandon') { return; }
      if (outcome === 'reload') {
        markReloadedFor(fresh, attr.value);
        window.location.reload();
        return;
      }
      renderMultiAll(reconcileNotice(failed, removeFailed));
      if (landed) {
        try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
      }
    }).catch(function () {
      // Degrade silently: she keeps the tick she already had and the next render retries.
      restoreBusy = false;
      rewardAddFailed = false;
      rewardRemoveFailed = false;
    });
    return restoreGate;
  }

  function maybeRestoreSelection(data, cart) {
    if (multiBusy || restoreBusy) { return; } // a /select round-trip or a restore already owns the cart
    if (!shouldRestoreSelection(data, cart)) { return; }
    restoreSelection(data, cart);
  }

  // ───────── annique-issues#218: the reward line is CONDITIONAL, so re-test the condition ─────────
  //
  // Withholding a below-floor reward line is only half an answer — the other half is what happens
  // when she then adds the goods that DO clear the floor. THIS is that half, and it is also the
  // cleanup the picker never had: `linesToRemove` used to run only inside `applySelection` and
  // `restoreSelection`, so an orphaned reward line could sit in the cart for ever because a plain
  // render never looked at it.
  //
  // `run()` re-fires on cart:change, cart:refresh, cart-drawer:refreshed and a bfcache pageshow, so
  // every basket edit re-tests every selected free_product voucher's floor:
  //   · goods now clear the floor          → the line she is owed is added
  //   · goods no longer clear the floor    → the line is removed before she is charged for it
  //   · her selection no longer names it   → the orphan is removed
  //
  // ⛔ WHY IT CANNOT LOOP, and this is the whole argument: `rewardFloorBaseCents` counts
  // buyer-funded lines ONLY. Every add or remove performed here touches a `_dbm_voucher_id` line,
  // which that number ignores — so no reconcile can change the verdict that produced it. Add once,
  // then `linesToAdd` sees the line and does nothing; remove once, then there is nothing to remove.
  // The fixed point is reached in one pass and re-entry is a no-op.
  function maybeReconcileRewardLines(data, cart) {
    if (multiBusy || restoreBusy || reconcileBusy) { return; } // someone else owns the cart
    var ensure = data && data.ensure_lines;
    var vouchers = data && data.vouchers;
    // #229 — the same honoured set the writer uses. The silence check has to agree with what would
    // actually be written, or the load path spends a request to discover there is nothing to do.
    var selectedSet = honouredSelection(lastSelected, vouchers, capOf(data));
    // Silence is the common case — never spend a request to discover there is nothing to do. An op
    // ALREADY TRIED this page view is silence too (#218 round 3, S1): without that clause a refused
    // add recomputed itself into existence on every single re-entry.
    if (!unattemptedOps(planRewardOps(cart, ensure, vouchers, selectedSet)).length) { return; }
    reconcileBusy = true;
    var startEpoch = selectionEpoch; // #218 r3 — her action supersedes this reconcile, both ways
    reconcileGate = reconcileRewardLines(cart, ensure, vouchers, function () {
      return selectionEpoch !== startEpoch;
    }).then(function (result) {
      return fetchCart().then(function (fresh) { return { result: result, cart: fresh }; });
    }).then(function (res) {
      reconcileBusy = false;
      if (res.cart) { lastCart = res.cart; }
      var failed = rewardAddFailed;
      var removeFailed = rewardRemoveFailed;
      rewardAddFailed = false;
      rewardRemoveFailed = false;
      // Rule 2 of the race, on the RESULT as well as the writes: she acted while this was in
      // flight, so her own round-trip owns the surface. Re-rendering from a pre-her cart, or
      // announcing it, would flicker back the state she just changed.
      if (selectionEpoch !== startEpoch) { return; }
      renderMultiAll(reconcileNotice(failed, removeFailed));
      // ⛔⛔ S1 — ANNOUNCE ONLY A CART THAT REALLY CHANGED. `cart:refresh` re-enters run(), and the
      // UNCONDITIONAL dispatch that used to sit here was the whole storm: the add or remove it was
      // waiting on had been REFUSED, so the short-circuit above could never fire, and every
      // dispatch bought another full round of /vouchers/available + cart.js + the refused write.
      // Triggers are ordinary — reward SKU sold out, variant unpublished, per-order limit met, a
      // stale variant id after a PIM re-import, a stale item.key.
      if (res.result && (res.result.added || res.result.removed)) {
        try { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
      }
    }).catch(function () {
      reconcileBusy = false;
      rewardAddFailed = false;
      rewardRemoveFailed = false;
    });
  }

  // ─────────── annique-issues#218 round 3: A GIFT SHE DELETES STAYS DELETED ───────────
  //
  // MEASURED on the shipped build: R499 of goods, the attribute already present so no restore runs,
  // the reward line absent — the load-path reconcile put it straight back. She removes the free
  // product, reloads, it is back. There was no sequence of actions that left it out.
  //
  // ⚠️ THIS IS A PRODUCT DECISION AND IT IS TAKEN HERE: DELETING THE REWARD LINE MEANS SHE DOES NOT
  // WANT THAT VOUCHER ON THIS ORDER. The line carries `_dbm_voucher_id` and nothing but this picker
  // ever puts it there, so a deliberate delete has no second reading. Both alternatives are worse:
  //   · silently re-add           — the cart fights her and there is no stable state to reach;
  //   · drop the line, keep the tick — the picker then says "selected" for a free_product voucher
  //     whose ENTIRE benefit IS that line. It is a no-op that still burns one of her two cap slots,
  //     so the lie on the surface costs her a real voucher on the same order.
  // So the delete DESELECTS the voucher, she is told why, and one tick puts the gift back.
  //
  // Telling "she deleted it" from "it was never added" needs one durable fact — that the line was
  // ONCE in this cart. That is the SEEN stamp, dropped again whenever WE remove the line ourselves
  // (see recordRewardOp), so our own floor-driven removal can never be read as her refusal.
  function ownedRewardIds(cart) {
    var ids = {};
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      var owner = item && item.properties && item.properties._dbm_voucher_id;
      if (owner) { ids[String(owner)] = true; }
    });
    return ids;
  }

  // PURE, and every clause is load-bearing. A voucher counts as REFUSED only when its line is gone
  // from a cart that HAD it, the voucher is still selected, and the floor is not the reason it is
  // absent (`addable` is the floor-cleared set, so that is checked by construction).
  function refusedRewardIds(cart, addable, selectedSet, seen, refused) {
    var present = ownedRewardIds(cart);
    var out = [];
    (Array.isArray(addable) ? addable : []).forEach(function (line) {
      var id = String(line.voucher_id);
      if (present[id]) { return; }      // still in the cart — nothing was refused
      if (!selectedSet[id]) { return; } // not hers to refuse any more
      if (!seen[id]) { return; }        // never added, so its absence is not a delete
      if (refused[id]) { return; }      // already acted on — at most once per cart + voucher
      out.push(id);
    });
    return out;
  }

  // Returns TRUE when it has taken over this turn: the caller must not then reconcile, or the
  // reconcile would add back the very line the refusal is about.
  function maybeHonourRewardRefusal(data, cart) {
    if (multiBusy || restoreBusy || reconcileBusy) { return false; }
    var vouchers = data && data.vouchers;
    var verdict = classifyEnsureLines(cart, data && data.ensure_lines, vouchers);
    // ⛔ #229 — THIS ONE STAYS ON THE RAW SELECTION, DELIBERATELY. It is not a cart write; it asks
    // "which reward line did SHE delete", and the answer must be read against everything she still
    // has ticked. The excess keeps its checked box ON PURPOSE (#218 round 3) so she can choose which
    // to drop — narrowing this to the honoured set would let a voucher she never refused be
    // deselected for her. A line WE remove is already protected: recordRewardOp drops its SEEN
    // stamp, so its absence never reads as her delete.
    var selectedSet = {};
    lastSelected.forEach(function (id) { selectedSet[String(id)] = true; });
    // Record what is in the cart NOW, so a later absence can be read for what it is.
    var present = ownedRewardIds(cart);
    Object.keys(present).forEach(function (id) { stampAdd(SEEN_KEY, stampKey(cart, id)); });
    var seen = {};
    var refused = {};
    verdict.add.forEach(function (line) {
      var id = String(line.voucher_id);
      seen[id] = stamped(SEEN_KEY, stampKey(cart, id));
      refused[id] = stamped(REFUSED_KEY, stampKey(cart, id));
    });
    var deleted = refusedRewardIds(cart, verdict.add, selectedSet, seen, refused);
    if (!deleted.length) { return false; }
    deleted.forEach(function (id) { stampAdd(REFUSED_KEY, stampKey(cart, id)); });
    var remaining = selectedOnList(lastSelected, vouchers).filter(function (id) {
      return deleted.indexOf(String(id)) === -1;
    });
    stashNotice(deleted.length === 1
      ? 'You removed the free product, so that voucher is no longer applied to this order. Tick it again to add the gift back.'
      : 'You removed the free products, so those vouchers are no longer applied to this order. Tick one again to add its gift back.');
    submitSelection(remaining, roots()[0]);
    return true;
  }

  // v0.3 auto_apply carries the single voucher's ID when exactly one is available and none selected.
  function maybeAutoSelect(data, cart) {
    if (data.auto_apply == null || data.vouchers.length !== 1) { return; }
    if (lastSelected.length) { return; }
    if (!cart || !cart.item_count) { return; } // nothing to discount yet
    var didAuto = false;
    try { didAuto = sessionStorage.getItem(AUTO_KEY) === String(data.auto_apply); } catch (e) {}
    if (didAuto) { return; }
    try { sessionStorage.setItem(AUTO_KEY, String(data.auto_apply)); } catch (e) {}
    submitSelection([data.auto_apply], roots()[0]);
  }

  // ──────────────────────────────────────────────────────────────────────────────────────

  // Cart on every device: while a saved-cart restore holds window.DBMCartLock, start after it instead of
  // racing it (snippets/dbm-cart-sync-config.liquid). Free cart, or no lock on the page: run now, unchanged.
  function deferWhileCartLocked(fn) {
    var lock = window.DBMCartLock;
    if (lock && typeof lock.busy === 'function' && lock.busy()) { lock.whenIdle(fn); return true; }
    return false;
  }

  function run() {
    if (deferWhileCartLocked(run)) { return; }
    if (!roots().length) { return; }
    if (multiBusy) { return; } // a /select round-trip owns the UI; it re-renders when done
    if (revealed) { reveal(); setAllState('loading', 'Loading your vouchers…'); }
    Promise.all([fetchAvailable(), fetchCart()]).then(function (res) {
      var available = res[0], cart = res[1];
      if (available && available.status === 404) {
        // Cutover tolerance: the voucher BFF is not deployed on this store (feature off) — render nothing.
        featureOff = true;
        conceal();
        return;
      }
      if (!available || available.network || !available.ok) {
        if (available && (available.status === 401 || available.status === 403)) {
          lastData = null; lastCart = cart; reveal(); setAllState('signed-out', 'Sign in to view and use your vouchers.'); return;
        }
        if (!revealed) { return; } // never shown on this store this page-view — degrade to nothing
        setAllState('retryable-error', 'We couldn’t load your vouchers right now.');
        return;
      }
      featureOff = false;
      var data = available.data || {};
      if (data.signed_in === false) {
        lastData = null; lastCart = cart; reveal(); setAllState('signed-out', 'Sign in to view and use your vouchers.'); return;
      }
      if (!Array.isArray(data.vouchers)) {
        if (!revealed) { return; } // not a voucher payload — treat like feature off, render nothing
        setAllState('retryable-error', 'The voucher service returned an invalid response.');
        return;
      }
      if (!cart) {
        if (!revealed) { return; }
        setAllState('retryable-error', 'We couldn’t read your cart right now.');
        return;
      }
      lastCart = cart;
      if (!data.vouchers.length) {
        lastData = null;
        reveal();
        setAllState('empty', 'You have no active vouchers right now.');
        return;
      }
      lastData = data;
      reveal();
      if (isMultiMode(data)) {
        lastSelected = Array.isArray(data.selected) ? data.selected.slice() : [];
        renderMultiAll(null);
        // #216 first: a surviving selection goes back on a cart that lost it. The two are mutually
        // exclusive by construction — restore needs a non-empty selection, auto-select needs an
        // empty one — so neither can undo the other.
        maybeRestoreSelection(data, cart);
        // #218 round 3 — a gift she DELETED stays deleted, and stops occupying a cap slot. This runs
        // BEFORE the reconcile because the reconcile is exactly what would put it back.
        if (maybeHonourRewardRefusal(data, cart)) { return; }
        // #218 — then re-test every selected free_product voucher's floor against THIS cart: add the
        // line she is now owed, and take back the one she must not be charged for.
        maybeReconcileRewardLines(data, cart);
        maybeAutoSelect(data, cart);
        return;
      }
      renderAll(cart);
      maybeAutoApply(data, cart);
    }).catch(function () {
      if (!revealed) { return; }
      setAllState('retryable-error', 'We couldn’t load your vouchers right now.');
    });
  }

  function init() {
    run();
    roots().forEach(function (container) {
      var retry = container.querySelector('[data-dbm-vouchers-retry]');
      if (retry) { retry.addEventListener('click', run); }
      var confirmApply = container.querySelector('[data-dbm-vouchers-confirm-apply]');
      if (confirmApply) { confirmApply.addEventListener('click', function () {
        if (!pendingSelection) { return; }
        var selection = pendingSelection; pendingSelection = null;
        var confirm = selection.container.querySelector('[data-dbm-vouchers-confirm]');
        if (confirm) { confirm.hidden = true; }
        applyVoucher(selection.voucher, selection.container);
      }); }
      var confirmCancel = container.querySelector('[data-dbm-vouchers-confirm-cancel]');
      if (confirmCancel) { confirmCancel.addEventListener('click', function () {
        pendingSelection = null;
        var confirm = container.querySelector('[data-dbm-vouchers-confirm]');
        if (confirm) { confirm.hidden = true; }
        renderAll(lastCart);
      }); }
    });
    // Clear-all is delegated so it survives the drawer re-rendering its DOM.
    document.addEventListener('click', function (e) {
      var target = e.target;
      var btn = target && target.closest ? target.closest('[data-dbm-vouchers-clear]') : null;
      if (!btn) { return; }
      var container = btn.closest('[data-dbm-vouchers]');
      if (!container || multiBusy || !lastData || !isMultiMode(lastData)) { return; }
      submitSelection([], container);
    });
    // Re-run on any cart mutation the theme announces (drawer open/refresh, line changes, our own applies).
    document.addEventListener('cart:refresh', run);
    document.addEventListener('cart:change', run);
    document.addEventListener('cart-drawer:refreshed', run);
    window.addEventListener('pageshow', function (e) { if (e.persisted) { run(); } });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
